import { FC, useState, useEffect } from 'react';
import cx from 'classnames/bind';
import { motion, AnimatePresence } from 'framer-motion';
import { useMediaQuery } from 'react-responsive';

import { Box } from '@/components/Box';
import { initialPosition, animatePosition } from '@/utils/getPositions';
import type { IBox } from '@/types/pack';

import styles from './Orders.module.scss';

interface OrdersProps {
  setStep: (value: number) => void;
  setIdPack: (value: number) => void;
  isViewModal?: boolean;
  currentList: string;
  setCurrentList: (value: string) => void;
  list: IBox[];
  isAllPointsReceived: boolean;
  firstList?: IBox[];
  secondList?: IBox[];
  thirdList?: IBox[];
  setModal: (value: string) => void;
  firstPlay: boolean;
}

const cn = cx.bind(styles);

const Orders: FC<OrdersProps> = ({
  setStep,
  setIdPack,
  isViewModal,
  currentList,
  setCurrentList,
  list,
  isAllPointsReceived,
  firstList,
  secondList,
  thirdList,
  setModal,
  firstPlay,
}) => {
  const isTablet = useMediaQuery({ query: '(min-width: 786px)' });

  const isFirstGame = list?.every(({ achievement }) => achievement === null);

  const isGameForPoints = list?.every(
    ({ achievement_without_score }) => achievement_without_score === null,
  );

  const isFirstGameCompleted = !isAllPointsReceived
    ? firstList?.every(({ achievement }) => achievement !== null)
    : firstList?.every(
        ({ achievement_without_score }) => achievement_without_score !== null,
      );

  const isSecondGameCompleted = !isAllPointsReceived
    ? secondList?.every(({ achievement }) => achievement !== null)
    : secondList?.every(
        ({ achievement_without_score }) => achievement_without_score !== null,
      );

  const isThirdGameCompleted = isGameForPoints
    ? thirdList?.every(({ achievement }) => achievement !== null)
    : thirdList?.every(
        ({ achievement_without_score }) => achievement_without_score !== null,
      );

  useEffect(() => {
    if (
      typeof isFirstGameCompleted === 'boolean' &&
      typeof isThirdGameCompleted === 'boolean' &&
      typeof isSecondGameCompleted === 'boolean' &&
      currentList === ''
    ) {
      const initialCurrentList = isThirdGameCompleted
        ? 'firstList'
        : isSecondGameCompleted
        ? 'thirdList'
        : isFirstGameCompleted
        ? 'secondList'
        : 'firstList';

      setCurrentList(initialCurrentList);
    }
  }, []);

  const [lastGameForPoints, setLastGameForPoints] = useState(false);

  const isGameWithoutPointsNotInChat = list?.every(
    ({ achievement_without_score }) => achievement_without_score === null,
  );

  return (
    <div className={cn('wrapper')}>
      {firstList && firstList.length > 0 && currentList === 'firstList' && (
        <AnimatePresence>
          <motion.ul
            initial={initialPosition(
              firstList,
              isTablet,
              firstPlay,
              isFirstGameCompleted,
            )}
            animate={animatePosition(isTablet, isFirstGameCompleted)}
            transition={{
              duration: 0.8,
              delay: isFirstGameCompleted ? 1.1 : 0.2,
            }}
            onAnimationComplete={() => {
              if (isFirstGame && !firstPlay) {
                setModal('collectOrder');
              }

              if (isFirstGameCompleted) {
                setCurrentList('secondList');
              }
            }}
            className={cn('list', 'list-three')}
          >
            {firstList.map(
              ({ pack, id, achievement, achievement_without_score }) => (
                <li className={cn('item')} key={id}>
                  <Box
                    text={pack.text}
                    src={pack.image}
                    achievement={achievement}
                    achievement_without_score={achievement_without_score}
                    setStep={setStep}
                    idPack={id}
                    setIdPack={setIdPack}
                    isAllPointsReceived={isAllPointsReceived}
                    lastGameForPoints={lastGameForPoints}
                  />
                </li>
              ),
            )}
          </motion.ul>
        </AnimatePresence>
      )}
      {secondList && secondList.length > 0 && currentList === 'secondList' && (
        <AnimatePresence>
          <motion.ul
            initial={initialPosition(
              secondList,
              isTablet,
              firstPlay,
              isSecondGameCompleted,
            )}
            animate={animatePosition(isTablet, isSecondGameCompleted)}
            transition={{
              duration: 0.8,
              delay: isSecondGameCompleted ? 1.1 : 0.2,
            }}
            onAnimationComplete={() => {
              if (isSecondGameCompleted) {
                setCurrentList('thirdList');
              }
            }}
            className={cn('list')}
          >
            {secondList.map(
              ({ pack, id, achievement, achievement_without_score }) => (
                <li className={cn('item')} key={id}>
                  <Box
                    text={pack.text}
                    src={pack.image}
                    achievement={achievement}
                    achievement_without_score={achievement_without_score}
                    setStep={setStep}
                    idPack={id}
                    setIdPack={setIdPack}
                    isAllPointsReceived={isAllPointsReceived}
                    lastGameForPoints={lastGameForPoints}
                  />
                </li>
              ),
            )}
          </motion.ul>
        </AnimatePresence>
      )}

      {thirdList && thirdList.length > 0 && currentList === 'thirdList' && (
        <AnimatePresence>
          <motion.ul
            initial={initialPosition(
              thirdList,
              isTablet,
              firstPlay,
              isThirdGameCompleted,
            )}
            animate={animatePosition(isTablet, isThirdGameCompleted)}
            transition={{
              duration: 0.8,
              delay: isThirdGameCompleted ? 1.1 : 0.2,
            }}
            onAnimationStart={() => {
              if (isGameWithoutPointsNotInChat) {
                setLastGameForPoints(true);
              }
            }}
            onAnimationComplete={() => {
              if (isThirdGameCompleted) {
                setCurrentList('firstList');
              }
              if (isViewModal && isAllPointsReceived) {
                setModal('pointsReceived');
              }

              if (isAllPointsReceived && isGameWithoutPointsNotInChat) {
                setLastGameForPoints(false);
              }
            }}
            className={cn('list', 'list-three')}
          >
            {thirdList.map(
              ({ pack, id, achievement, achievement_without_score }) => (
                <li className={cn('item')} key={id}>
                  <Box
                    text={pack.text}
                    src={pack.image}
                    achievement={achievement}
                    achievement_without_score={achievement_without_score}
                    setStep={setStep}
                    idPack={id}
                    setIdPack={setIdPack}
                    isAllPointsReceived={isAllPointsReceived}
                    lastGameForPoints={lastGameForPoints}
                  />
                </li>
              ),
            )}
          </motion.ul>
        </AnimatePresence>
      )}
    </div>
  );
};

export default Orders;
